import { Atom, atom, Getter } from "jotai";
import type { grossRevenueItemization } from "../projections";
import { CellTypes } from "../types";

export type ItemizationAtom = typeof grossRevenueItemization;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SpreadsheetCellStyle = {
  fill?: { fgColor?: { rgb?: string } };
  font?: {
    bold?: boolean;
    color?: { rgb?: string };
    sz?: number;
    italic?: boolean;
  };
  border?: {
    top?: { style?: string; color?: { rgb?: string } };
    bottom?: { style?: string; color?: { rgb?: string } };
    left?: { style?: string; color?: { rgb?: string } };
    right?: { style?: string; color?: { rgb?: string } };
  };
};

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

export const boldStyle = {
  font: { bold: true },
} satisfies SpreadsheetCellStyle;

export const singleTopBorderStyle = {
  font: { bold: true },
  border: {
    top: { style: "thin", color: { rgb: "000000" } },
  },
} satisfies SpreadsheetCellStyle;

export const doubleTopBorderStyle = {
  font: { bold: true },
  border: {
    top: { style: "double", color: { rgb: "000000" } },
  },
} satisfies SpreadsheetCellStyle;

export const italicStyle = {
  font: { italic: true, color: { rgb: "002060" }, sz: 10 },
} satisfies SpreadsheetCellStyle;

export const greyHighlightStyle = {
  font: { color: { rgb: "000000" }, bold: true },
  fill: { fgColor: { rgb: "D9D9D9" } },
  border: {
    top: { style: "thin", color: { rgb: "555555" } },
    bottom: { style: "thin", color: { rgb: "555555" } },
    left: { style: "thin", color: { rgb: "555555" } },
    right: { style: "thin", color: { rgb: "555555" } },
  },
} satisfies SpreadsheetCellStyle;

// ---------------------------------------------------------------------------
// Pure row-creation helpers
// ---------------------------------------------------------------------------

export const createTitleRow = (title: string) => {
  const s = {
    fill: { fgColor: { rgb: "002060" } },
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 14 },
  };

  return [
    { v: title, s },
    // copy the style across the row for 100 columns
    ...Array.from({ length: 100 }, () => ({ v: "", s })),
  ];
};

export const createEmptyRow = () => [""];

export const createGroupStartRow = () => ["GROUP_START"];

export const createGroupEndRow = () => ["GROUP_END"];

// ---------------------------------------------------------------------------
// get-dependent helpers (accept Jotai's Getter so they can be pure functions)
// ---------------------------------------------------------------------------

export const toSpreadsheetCell = (
  get: Getter,
  cellAtom: Atom<CellTypes> | string,
  options?: {
    type?: "percent" | "currency" | "dscr" | "string";
    isNegative?: boolean;
    style?: SpreadsheetCellStyle;
  },
) => {
  if (typeof cellAtom === "string") {
    return {
      v: cellAtom,
      t: "s",
      z: undefined,
    };
  }

  const cell = get(cellAtom);

  let type = options?.type || cell?.type;
  if (type === "growth") type = "percent";

  const value =
    Number(cell?.value) * (options?.isNegative && type !== "percent" ? -1 : 1);

  const isNumeric = !isNaN(value);
  let formattedValue = isNumeric ? Number(value.toFixed(2)) : cell?.value;

  let z: string | undefined = '"$"#,##0;[Red]("$"#,##0)';
  let t = "n";

  if (type === "percent") {
    z = "0.00%";
    formattedValue = Number(value);
    if (!(value > 0)) {
      z = "";
      formattedValue = "";
    }
  } else if (type === "currency") {
    z = '"$"#,##0;[Red]("$"#,##0)';
  } else if (type === "dscr") {
    z = "0.00x";
  } else if (type === "string") {
    t = "s";
    z = undefined;
  }

  return {
    v: formattedValue,
    t,
    z,
    s: options?.style,
  };
};

export const moleculeToRow = (
  get: Getter,
  molecule: Atom<Atom<CellTypes>[]>,
  options?: {
    isNegative?: boolean;
    label?: string;
    style?: SpreadsheetCellStyle;
    type?: "percent" | "currency" | "dscr" | "string";
  },
) => {
  const cells = get(molecule).map((cell) =>
    toSpreadsheetCell(get, cell, {
      ...options,
      //type: i === 1 ? "percent" : options?.type || "currency",
    }),
  );

  return options?.label
    ? [toSpreadsheetCell(get, options.label, options), ...cells]
    : cells;
};

export const itemizationToRows = (
  get: Getter,
  label: string,
  itemizationAtom: ItemizationAtom,
  options?: { isNegative?: boolean },
) => {
  const itemization = get(itemizationAtom);

  const nullAtom = atom(null);
  const isActive = get(itemization.activeAtom);
  const growthRateMode = get(
    itemization.growthRateContainer?.modeAtom || nullAtom,
  );
  const rows = [];

  if (!itemization.growthRateContainer) {
    rows.push(
      moleculeToRow(get, itemization.summarizedMolecule, {
        ...options,
        label,
      }),
    );
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "compoundAnnual" &&
    !isActive
  ) {
    rows.push(
      moleculeToRow(
        get,
        itemization.growthRateContainer.compoundAnnualMolecule,
        {
          ...options,
          label,
        },
      ),
    );
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "compoundAnnual" &&
    isActive
  ) {
    rows.push(
      moleculeToRow(get, itemization.aggregatedColumnsMolecule, {
        ...options,
        label,
      }),
    );
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "compoundAnnual" &&
    isActive
  ) {
    rows.push(
      moleculeToRow(get, itemization.summarizedYoyMolecule, {
        ...options,
        label: label + " Growth Rate (%)",
        style: italicStyle,
        type: "percent",
      }),
    );
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "perYear" &&
    !isActive
  ) {
    rows.push(
      moleculeToRow(get, itemization.growthRateContainer.perYearYoYMolecule, {
        ...options,
        label,
      }),
    );
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "perYear" &&
    isActive
  ) {
    rows.push(
      moleculeToRow(get, itemization.aggregatedColumnsMolecule, {
        ...options,
        label,
      }),
    );
  }

  if (itemization.growthRateContainer && growthRateMode === "perYear") {
    rows.push(
      moleculeToRow(get, itemization.growthRateContainer.perYearMolecule, {
        ...options,
        label: label + " Growth Rate (%)",
        style: italicStyle,
        type: "percent",
      }),
    );
  }

  if (isActive) {
    const lineItems = itemization.lineItems;

    rows.push(createGroupStartRow());
    lineItems.forEach((lineItem, index) => {
      const row = moleculeToRow(get, itemization.lineItemsMolecules[index], {
        ...options,
        label: lineItem.label,
      });

      rows.push(row);
    });
    rows.push(createGroupEndRow());
  }

  if (
    itemization.growthRateContainer &&
    growthRateMode === "compoundAnnual" &&
    !isActive
  ) {
    rows.push(
      moleculeToRow(
        get,
        itemization.growthRateContainer.compoundAnnualYoYMolecule,
        {
          ...options,
          label: label + " Growth Rate (%)",
          style: italicStyle,
          type: "percent",
        },
      ),
    );
  }

  return rows;
};

// ---------------------------------------------------------------------------
// Worksheet helpers
// ---------------------------------------------------------------------------

export const groupWorksheetRows = (
  ws: { "!rows"?: { hpx?: number; level?: number }[] },
  start_row: number,
  end_row: number,
) => {
  if (!ws["!rows"]) ws["!rows"] = [];
  for (let i = start_row; i <= end_row; ++i) {
    if (!ws["!rows"][i]) ws["!rows"][i] = { hpx: 20 };
    ws["!rows"][i].level = 1 + (ws["!rows"][i].level || 0);
  }
};
